/**
 * InternalNotesSection Component
 * Extracted from SessionDetailModal — internal Medley team notes with RichTextEditor.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Stack,
  Group,
  Text,
  Button,
  Loader,
  Center,
  Alert,
  Divider,
  Paper,
  Avatar,
  UnstyledButton,
} from '@mantine/core';
import { AlertCircle, Plus } from 'react-feather';
import { sessionsApi } from '../../../../services/api/sessions';
import type { SessionNoteWithAuthor } from '../../../../types';
import { formatDateTime } from '../../../../utils/formatters';
import { RichTextEditor } from '../../../../components/common/RichTextEditor/RichTextEditor';
import { RichTextContent } from '../../../../components/common/RichTextEditor/RichTextContent';

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

interface InternalNotesSectionProps {
  cohortId: string;
  sessionId: string;
  onNotesChanged?: (count: number) => void;
  /**
   * MED-1360: hides Add Note. The server lets a coach write a note only on a
   * session they coach, so the drawer sets this for any other session.
   */
  readOnly?: boolean;
}

export function InternalNotesSection({
  cohortId,
  sessionId,
  onNotesChanged,
  readOnly = false,
}: InternalNotesSectionProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState<SessionNoteWithAuthor[]>([]);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  const loadNotes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await sessionsApi.getNotes(cohortId, sessionId);
      setNotes(data.notes);
      onNotesChanged?.(data.notes.length);
    } catch {
      setError('Failed to load notes');
    } finally {
      setLoading(false);
    }
  }, [cohortId, sessionId, onNotesChanged]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const isContentEmpty = (html: string): boolean => {
    const stripped = html.replace(/<[^>]*>/g, '').trim();
    return stripped.length === 0;
  };

  const handleAddNote = async () => {
    if (isContentEmpty(newNoteContent)) return;

    setSaving(true);
    setError(null);
    try {
      const newNote = await sessionsApi.createNote(cohortId, sessionId, newNoteContent.trim());
      const updatedNotes = [newNote, ...notes];
      setNotes(updatedNotes);
      setNewNoteContent('');
      setIsEditorOpen(false);
      onNotesChanged?.(updatedNotes.length);
    } catch {
      setError('Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    );
  }

  return (
    <Stack gap="md">
      {error && (
        <Alert icon={<AlertCircle size={16} />} color="red" variant="light">
          {error}
        </Alert>
      )}

      {/* Add Note Form */}
      {readOnly ? null : isEditorOpen ? (
        <Stack gap="xs">
          <Text size="sm" fw={500}>
            Add a Note
          </Text>
          <RichTextEditor
            value={newNoteContent}
            onChange={setNewNoteContent}
            placeholder="Add notes about this session... (internal only, visible to Medley team)"
            minHeight={150}
          />
          <Group justify="flex-end">
            <Button
              size="sm"
              variant="subtle"
              color="gray"
              onClick={() => {
                setNewNoteContent('');
                setIsEditorOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleAddNote}
              loading={saving}
              disabled={isContentEmpty(newNoteContent)}
            >
              Add Note
            </Button>
          </Group>
        </Stack>
      ) : (
        <UnstyledButton
          onClick={() => setIsEditorOpen(true)}
          p="sm"
          style={{
            borderRadius: '8px',
            border: '2px dashed #D0CEC9',
            backgroundColor: 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            color: 'var(--mantine-color-gray-6)',
            width: '100%',
          }}
        >
          <Plus size={16} />
          <Text size="sm" fw={500}>Add Note</Text>
        </UnstyledButton>
      )}

      {!readOnly && <Divider />}

      {/* Notes List */}
      <Stack gap="sm">
        <Text size="sm" fw={500}>
          Notes ({notes.length})
        </Text>

        {notes.length === 0 ? (
          <Paper withBorder p="lg" style={{ borderStyle: 'dashed' }}>
            <Text size="sm" c="dimmed" ta="center">
              {readOnly ? 'No notes yet.' : 'No notes yet. Add the first note above.'}
            </Text>
          </Paper>
        ) : (
          <Stack gap="sm">
            {notes.map((note) => (
              <Paper key={note.id} withBorder p="md">
                <Stack gap="xs">
                  <Group justify="space-between">
                    <Group gap="xs">
                      <Avatar size="sm" radius="xl" color="blue">
                        {getInitials(note.author_name)}
                      </Avatar>
                      <div>
                        <Text size="sm" fw={500}>
                          {note.author_name}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {note.author_email}
                        </Text>
                      </div>
                    </Group>
                    <Text size="xs" c="dimmed">
                      {formatDateTime(note.created_at)}
                    </Text>
                  </Group>
                  <RichTextContent content={note.content} />
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}

export default InternalNotesSection;
